/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import {
  EuiLink,
  EuiButton,
  EuiButtonEmpty,
  EuiPageHeader,
  EuiSkeletonText,
  EuiSpacer,
  EuiText,
  EuiToolTip,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import Breadcrumbs from '@modules/cui/Breadcrumbs/Breadcrumbs'
import { projectCpsCrumbs } from '@modules/project-user-pages/breadcrumbs'
import { projectCpsLinkUrl } from '@modules/project-user-lib/urls'
import type { ProjectID, ProjectType } from '@modules/ui-types/projects'
import ProjectApiErrorCallout from '@modules/project-components/ProjectApiErrorCallout'
import usePermissions from '@modules/permissions-lib/hooks'
import { editDefaultSpacesUrl } from '@modules/project-lib/kibanaLinks'

import { useProjectCpsContext } from './ProjectCpsContext'
import ProjectCpsEmptyPage from './ProjectCpsEmptyPage'
import LinkedProjectsList from './LinkedProjectsList'
import ProjectCpsNoPermissionCallout from './ProjectCpsNoPermissionCallout'
import ProjectCpsSpacesConfigurationCallout from './ProjectCpsSpacesConfigurationCallout'
import { useIsCpsFeatureActive } from './hooks'

export const MAX_LINKED_PROJECTS = 20

type Props = {
  projectType: ProjectType
}

const ProjectCpsPage: React.FunctionComponent<Props> = ({ projectType }) => {
  const { projectId } = useParams() as { projectId: ProjectID }
  const navigate = useNavigate()
  const { useGetProjectQuery } = useProjectCpsContext()
  const isCpsEnabled = useIsCpsFeatureActive()

  const { data: project, error: projectError } = useGetProjectQuery(projectType, projectId) ?? {}

  const { hasPermissions: hasUpdatePermission } = usePermissions([
    { type: `project-${projectType}`, action: 'update', id: projectId },
  ])

  if (!isCpsEnabled) {
    return <ProjectApiErrorCallout error='Cross-project search feature is not enabled' />
  }

  if (projectError) {
    return <ProjectApiErrorCallout error={projectError} />
  }

  if (!project) {
    return <EuiSkeletonText />
  }

  // TODO: check status of linked projects?
  const linkedProjects = project.linked?.projects || {}
  const hasLinkedProjects = Object.keys(linkedProjects).length > 0

  if (!hasLinkedProjects) {
    return (
      <ProjectCpsEmptyPage
        projectType={projectType}
        projectId={projectId}
        projectName={project.name}
        hasUpdatePermission={hasUpdatePermission}
      />
    )
  }

  const pageTitle = <FormattedMessage id='projectCps.title' defaultMessage='Cross-project search' />

  const intro = (
    <FormattedMessage
      id='projectCps.intro'
      defaultMessage='Search and analyze data across multiple projects from the {projectName} project. {learnMoreLink}'
      values={{
        projectName: (
          <EuiText color='accent' component='span'>
            {project.name}
          </EuiText>
        ),
        learnMoreLink: (
          // TODO: https://github.com/elastic/cloud-ui/issues/952
          <EuiLink
            external={true}
            href='https://www.elastic.co/TBD'
            target='_target'
            data-test-subj='cps-page-learnMoreLink'
          >
            <FormattedMessage id='projectCps.learnMore' defaultMessage='Learn more' />
          </EuiLink>
        ),
      }}
    />
  )

  // TODO: check status of linked projects?
  const linkedProjectsCount = Object.keys(linkedProjects).length
  const isMaxLinkedProjects = linkedProjectsCount >= MAX_LINKED_PROJECTS
  const hasNoUpdatePermission = !hasUpdatePermission

  const manageSpaceButton = (
    <EuiButtonEmpty
      iconType='popout'
      iconSide='right'
      href={editDefaultSpacesUrl(project)}
      target='_blank'
      data-test-subj='cps-page-manageSpaceLink'
    >
      <FormattedMessage id='projectCps.manageSpaceLink' defaultMessage='Manage Space' />
    </EuiButtonEmpty>
  )

  const linkProjectsButton = (
    <EuiButton
      fill={true}
      onClick={() => navigate(projectCpsLinkUrl(projectType, projectId))}
      disabled={isMaxLinkedProjects || hasNoUpdatePermission}
      data-test-subj='cps-page-linkProjectsButton'
    >
      <FormattedMessage id='projectCps.linkProjectsButton' defaultMessage='Link projects' />
    </EuiButton>
  )

  const linkProjectsButtonWithTooltip = () => {
    if (isMaxLinkedProjects) {
      return (
        <EuiToolTip
          content={
            <FormattedMessage
              id='projectCps.maxLinkedProjectsTooltip'
              defaultMessage="You've already linked all available projects."
            />
          }
          position='top'
        >
          {linkProjectsButton}
        </EuiToolTip>
      )
    }

    if (hasNoUpdatePermission) {
      return (
        <EuiToolTip
          content={
            <FormattedMessage
              id='projectCps.noPermissionTooltip'
              defaultMessage='No permission to link projects'
            />
          }
          position='top'
        >
          {linkProjectsButton}
        </EuiToolTip>
      )
    }

    return linkProjectsButton
  }

  const rightSideItems = [
    linkProjectsButtonWithTooltip(),
    ...(hasNoUpdatePermission ? [] : [manageSpaceButton]),
  ]

  return (
    <Fragment>
      <Breadcrumbs
        breadcrumbs={projectCpsCrumbs({
          projectType,
          projectId,
          projectName: project.name,
        })}
      />

      {hasNoUpdatePermission && (
        <Fragment>
          <ProjectCpsNoPermissionCallout />
          <EuiSpacer size='m' />
        </Fragment>
      )}

      {!hasNoUpdatePermission && (
        <Fragment>
          <ProjectCpsSpacesConfigurationCallout project={project} />
          <EuiSpacer size='m' />
        </Fragment>
      )}

      <EuiPageHeader
        pageTitle={pageTitle}
        description={intro}
        bottomBorder={true}
        rightSideItems={rightSideItems}
      />

      <EuiSpacer />

      <LinkedProjectsList
        linkedProjects={linkedProjects}
        projectId={projectId}
        projectType={projectType}
        projectName={project.name}
      />
    </Fragment>
  )
}

export default ProjectCpsPage
